import { SearchRounded } from "@mui/icons-material";
import AddCircleOutline from "@mui/icons-material/AddCircleOutline";
import {
  Box,
  Button,
  InputAdornment,
  TextField,
  Typography,
} from "@mui/material";
import { useGridApiRef } from "@mui/x-data-grid";
import { useQuery } from "@tanstack/react-query";

import { useEffect, useState } from "react";
import useAPI from "../../../api/hooks/useAPI";
import useColors from "../../../api/hooks/useColors";
import { platformUsersKeys } from "../../../api/queryKeys";
import ExportButton from "../../../components/buttons/ExportButton";
import FilterMenu from "../../../components/filters/FilterMenu";
import { useFileNaming } from "../../../hooks/useFileNaming";
import {
  UserPlatformAccessRole,
  UserPlatformTitle,
} from "../../../utils/constants";
import { searchObjectProperties } from "../../../utils/search";
import InvitePlatformUser from "./InvitePlatformUser";
import PlatformUsersTable from "./PlatformUsersTable";

const Members = () => {
  const colors = useColors();
  const { getAllPlatformUsers, trackEvent, getPlatformUser } = useAPI();
  const { generateBreezeFileName } = useFileNaming();
  const gridRef = useGridApiRef();

  const [openInviteDialog, setOpenInviteDialog] = useState(false);

  const handleClose = () => {
    setOpenInviteDialog(false);
    const message = "User close the add user dialog";
    trackEvent(message).then();
  };

  const platformUsersList = useQuery({
    queryKey: platformUsersKeys.all,
    queryFn: getAllPlatformUsers,
    select: (response) => response.data,
  });

  const currentPlatformUser = useQuery({
    queryKey: platformUsersKeys.user(),
    queryFn: getPlatformUser,
    select: (response) => response.data,
  });

  const handleTableExport = () => {
    trackEvent("user export the platform users list");
    const csvExportOptions = {
      fileName: generateBreezeFileName(`platform_users`),
    };
    gridRef?.current?.exportDataAsCsv(csvExportOptions);
  };

  const [appliedFilters, setAppliedFilters] = useState({
    access_role: {},
    title: {},
    searchString: "",
  });

  const [filterOption, setFilterOptions] = useState({
    access_role: [],
    title: [],
  });

  useEffect(() => {
    const existingTitles = new Set();
    const existingRolePrivilege = new Set();
    platformUsersList?.data?.forEach((user) => {
      existingTitles.add(user.role);
      let displayRole = user.access_role;
      if (displayRole === UserPlatformAccessRole.MSSP)
        displayRole = UserPlatformAccessRole.ADMIN;
      else if (displayRole === UserPlatformAccessRole.MSSP_VIEWER)
        displayRole = UserPlatformAccessRole.VIEWER;
      existingRolePrivilege.add(displayRole);
    });
    setFilterOptions({
      ...filterOption,
      title: Array.from(existingTitles),
      access_role: Array.from(existingRolePrivilege),
    });
  }, [platformUsersList?.data]);

  const [filterUsers, setFilterUsers] = useState(platformUsersList);

  useEffect(() => {
    trackEvent(`user change filters to ${JSON.stringify(appliedFilters)}`);
    if (platformUsersList) {
      let filteredRows = platformUsersList?.data;
      // Filter out fake users. they should not be visible in the UI
      filteredRows = filteredRows?.filter((user) => user?.is_visible !== false);
      if (Object.keys(appliedFilters.title)?.length > 0) {
        const title_filter = Object.keys(appliedFilters.title).filter(
          (key) => appliedFilters.title[key] === true
        );
        if (title_filter.length > 0) {
          filteredRows = filteredRows.filter((user) =>
            title_filter.includes(user.role)
          );
        }
      }
      if (Object.keys(appliedFilters.access_role)?.length > 0) {
        const access_role_filter = Object.keys(
          appliedFilters.access_role
        ).filter((key) => appliedFilters.access_role[key] === true);
        if (access_role_filter.length > 0) {
          filteredRows = filteredRows.filter((user) => {
            let userRole = user.access_role;
            if (userRole === UserPlatformAccessRole.MSSP)
              userRole = UserPlatformAccessRole.ADMIN;
            else if (userRole === UserPlatformAccessRole.MSSP_VIEWER)
              userRole = UserPlatformAccessRole.VIEWER;
            return access_role_filter.includes(userRole);
          });
        }
      }
      if (appliedFilters.searchString !== "") {
        const lowerCaseSearchString = appliedFilters.searchString.toLowerCase();
        filteredRows = filteredRows.filter((row) =>
          searchObjectProperties(row, lowerCaseSearchString)
        );
      }
      setFilterUsers(filteredRows);
    }
  }, [appliedFilters, platformUsersList.data]);

  const existingUserTitles =
    platformUsersList?.data?.map((user) => user.role) ?? [];
  const titleOptions = Array.from(
    new Set([...existingUserTitles, ...UserPlatformTitle])
  ).sort();

  return (
    <Box ml={"16px"} display="flex" flexDirection="column" gap={1}>
      <Typography variant="subtitle1">Members</Typography>
      <Box
        marginTop={1}
        sx={{
          display: "flex",
          justifyContent: "space-between",
        }}
      >
        <Box sx={{ display: "flex", gap: 1 }}>
          <TextField
            InputProps={{
              sx: {
                color: colors.contrast_text_dark,
                fontSize: "14px",
              },
              startAdornment: (
                <InputAdornment position="start">
                  <SearchRounded
                    style={{
                      color: colors.text_disabled,
                      width: "16px",
                      height: "16px",
                    }}
                  />
                </InputAdornment>
              ),
            }}
            placeholder="Search"
            type="text"
            value={appliedFilters.searchString}
            onChange={(e) => {
              setAppliedFilters({
                ...appliedFilters,
                searchString: e.target.value,
              });
            }}
            sx={{
              "& .MuiOutlinedInput-notchedOutline": {
                borderRadius: "8px",
              },
              "& .MuiInputBase-root": { height: "32px" },
            }}
          />
          <FilterMenu
            filterName={"Title"}
            fieldName={"title"}
            options={filterOption.title.map((title) => {
              return {
                name: title,
                label: title,
                displayName: title,
              };
            })}
            appliedFilters={appliedFilters}
            setAppliedFilters={setAppliedFilters}
            fullOptions={filterOption.title}
          />
          <FilterMenu
            filterName={"Role"}
            fieldName={"access_role"}
            options={filterOption.access_role.map((access_role) => {
              return {
                name: access_role,
                label: access_role,
                displayName: access_role,
              };
            })}
            appliedFilters={appliedFilters}
            setAppliedFilters={setAppliedFilters}
            fullOptions={filterOption.access_role}
          />
        </Box>
        <Box sx={{ display: "flex", gap: 1 }}>
          {currentPlatformUser.data?.is_admin && (
            <Button
              sx={{
                color: colors.text_primary,
                padding: "0px 8px",
                height: "32px",
                border: "1px solid " + colors.divider2,
                textTransform: "unset",
                borderRadius: "8px",
                cursor: "pointer",
              }}
              startIcon={<AddCircleOutline />}
              onClick={() => setOpenInviteDialog(true)}
            >
              <Typography variant="caption">Invite</Typography>
            </Button>
          )}
          <ExportButton handleExport={handleTableExport} />
        </Box>
      </Box>
      <Box mt={1}>
        <PlatformUsersTable
          platformUsersList={filterUsers}
          currentPlatformUser={currentPlatformUser}
          refToPass={gridRef}
          titleOptions={titleOptions}
        />
      </Box>
      <InvitePlatformUser
        open={openInviteDialog}
        handleClose={handleClose}
        titleOptions={titleOptions}
      />
    </Box>
  );
};
export default Members;
